import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { BRAND, getBandLabel, uiText, getCategoryLabel, scoreToBand, BAND_COLORS } from './types';
import { ScoreRing } from './ScoreRing';
import { ProgressBar } from './ProgressBar';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);
function HistoryChart({ category, reports, locale }) {
    // Oldest first for chronological display
    const sorted = [...reports].reverse();
    const isOverall = category === '__overall__';
    const labels = sorted.map(r => new Date(r.generatedAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: '2-digit' }));
    const overallScores = sorted.map(r => r.overallScore);
    const categoryScores = isOverall ? null : sorted.map(r => r.categoryScores[category] ?? null);
    const datasets = isOverall
        ? [
            {
                label: uiText('score.overall', locale),
                data: overallScores,
                borderColor: BRAND.blue,
                backgroundColor: `${BRAND.blue}18`,
                fill: false,
                tension: 0.3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: overallScores.map(s => BAND_COLORS[scoreToBand(s)]),
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                borderWidth: 2.5,
                spanGaps: true,
            },
        ]
        : [
            {
                label: getCategoryLabel(category, locale),
                data: categoryScores,
                borderColor: BRAND.blue,
                backgroundColor: `${BRAND.blue}18`,
                fill: false,
                tension: 0.3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: categoryScores.map(s => s !== null ? BAND_COLORS[scoreToBand(s)] : BRAND.blue),
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                borderWidth: 2.5,
                spanGaps: true,
            },
            {
                label: uiText('score.overall', locale),
                data: overallScores,
                borderColor: '#9ca3af',
                backgroundColor: 'transparent',
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: '#9ca3af',
                pointBorderColor: '#fff',
                pointBorderWidth: 1.5,
                borderWidth: 1.5,
                borderDash: [4, 3],
            },
        ];
    const data = { labels, datasets };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                min: 0,
                max: 100,
                ticks: { stepSize: 25, color: '#9ca3af', font: { size: 10 } },
                grid: { color: '#f3f4f6' },
                border: { display: false },
            },
            x: {
                ticks: { color: '#9ca3af', font: { size: 10 } },
                grid: { display: false },
                border: { display: false },
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: { boxWidth: 12, font: { size: 10 }, color: '#6b7280', padding: 12 },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label ?? ''}: ${ctx.parsed.y ?? '–'}`,
                },
            },
        },
    };
    return (<div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">{uiText('detail.history', locale)}</h3>
            <div style={{ height: 180 }}>
                <Line data={data} options={options}/>
            </div>
        </div>);
}
export function CategoryDetailPanel({ category, entry, measurementTargets, reports, onClose, audience = 'trainer', locale = 'de' }) {
    const isOverall = category === '__overall__';
    const band = scoreToBand(entry.score);
    const text = !isOverall
        ? (audience === 'customer' ? (entry.interpretation?.customer ?? null) : (entry.interpretation?.trainer ?? null))
        : null;
    const showHistory = reports.length > 1;
    return (<div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={(e) => { if (e.target === e.currentTarget)
        onClose(); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" style={{ borderTop: `4px solid ${BRAND.blue}` }}>
                <div className="flex items-center justify-between p-5 pb-3">
                    <div className="flex items-center gap-3">
                        <ScoreRing score={entry.score} size={64} strokeWidth={7}/>
                        <div>
                            <h2 className="text-lg font-bold" style={{ color: BRAND.blueDeep }}>
                                {getCategoryLabel(category, locale)}
                            </h2>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="inline-block rounded-full w-2.5 h-2.5" style={{ backgroundColor: BAND_COLORS[band] }}/>
                                <span className="text-sm text-gray-500">{getBandLabel(band, locale)}</span>
                                {!isOverall && entry.trainerScore !== undefined && (<span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>{uiText('detail.measured', locale)}</span>)}
                            </div>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-full text-gray-400 hover:bg-gray-100 transition-colors" aria-label="Schließen">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M14 4L4 14M4 4l10 10"/>
                        </svg>
                    </button>
                </div>

                <div className="px-5 pb-5 flex flex-col gap-4">
                    {text !== null && (<div className="rounded-lg p-3" style={{ backgroundColor: BRAND.tint }}>
                            <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                        </div>)}

                    {!isOverall && entry.fragebogenScore !== undefined && entry.trainerScore !== undefined && (<div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-gray-100 p-3 text-center">
                                <p className="text-xs text-gray-400 mb-1">{uiText('detail.questionnaire', locale)}</p>
                                <p className="text-xl font-bold" style={{ color: BRAND.blue }}>{entry.fragebogenScore}</p>
                                <ProgressBar score={entry.fragebogenScore} height={4}/>
                            </div>
                            <div className="rounded-lg border border-gray-100 p-3 text-center">
                                <p className="text-xs text-gray-400 mb-1">{uiText('detail.trainerMeasurement', locale)}</p>
                                <p className="text-xl font-bold" style={{ color: BRAND.blue }}>{entry.trainerScore}</p>
                                <ProgressBar score={entry.trainerScore} height={4}/>
                                {entry.isPartial === true && <p className="text-xs text-amber-500 mt-1">{uiText('detail.partialMeasurement', locale)}</p>}
                            </div>
                        </div>)}

                    {measurementTargets.length > 0 && (<div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">{uiText('detail.valuesAndTargets', locale)}</h3>
                            <div className="flex flex-col gap-2">
                                {measurementTargets.map(t => (<div key={t.label} className="rounded-lg border border-gray-100 p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">{t.label}</span>
                                            <span className="inline-block rounded-full w-2 h-2" style={{ backgroundColor: BAND_COLORS[scoreToBand(t.currentScore)] }}/>
                                        </div>
                                        <div className="flex items-baseline gap-1 text-sm">
                                            <span className="font-bold">{t.currentValue.toFixed(1)}</span>
                                            {t.unit !== null && <span className="text-gray-400 text-xs">{t.unit}</span>}
                                            <span className="text-xs text-gray-400 ml-1">(Score {t.currentScore})</span>
                                            {t.targetValue !== null ? (<>
                                                    <span className="text-gray-300 mx-1">→</span>
                                                    <span className="text-green-600 font-bold">{t.targetValue.toFixed(1)}</span>
                                                    {t.unit !== null && <span className="text-gray-400 text-xs">{t.unit}</span>}
                                                    <span className="text-xs text-gray-400 ml-1">{uiText('detail.forScore', locale)} {t.targetScore}</span>
                                                </>) : (<span className="text-green-600 text-xs ml-1">✓ {uiText('measurementTargets.reached', locale)}</span>)}
                                        </div>
                                    </div>))}
                            </div>
                        </div>)}

                    {showHistory && (<HistoryChart category={category} reports={reports} locale={locale}/>)}
                </div>
            </div>
        </div>);
}
